import React, { useState } from 'react';
import { CHROME, SERIES, fmt, fmtCompact, niceCeil } from './vizTokens';

const PLOT_HEIGHT = 168;
const BAR_MAX_WIDTH = 24;   // marks stay thin; the band's leftover is air
const SEGMENT_GAP = 2;      // surface gap — never a border drawn around a mark

const LEGEND = [
  { key: 'new', label: 'New', color: SERIES.new },
  { key: 'modified', label: 'Modified', color: SERIES.modified },
];

/**
 * 7-day sync activity as a stacked column chart.
 *
 * Two series rather than one combined total: "new" and "modified" are different
 * events, and the previous single bar hid which one moved. Colour follows the
 * series, not the column's position — the old chart painted today's bar a
 * different blue, which encodes rank instead of identity and makes the legend a
 * lie on any other day.
 *
 * "Deleted" and "errors" are not bars: a third series failed the contrast gate
 * against this surface, and both are better read as exceptions than as volume.
 * They stay in the tooltip and in the table view, so no value is gated behind
 * hover.
 */
const SyncActivityChart = ({ weekly }) => {
  const [active, setActive] = useState(null);
  const [showTable, setShowTable] = useState(false);

  if (!weekly?.length) return null;

  const axisTop = niceCeil(Math.max(...weekly.map((d) => (d.new || 0) + (d.modified || 0)), 1));
  const ticks = [axisTop, axisTop / 2, 0];
  const peakIndex = weekly.reduce(
    (best, d, i) => ((d.new || 0) + (d.modified || 0) > (weekly[best].new || 0) + (weekly[best].modified || 0) ? i : best),
    0
  );
  const isEmpty = weekly.every((d) => !(d.new || 0) && !(d.modified || 0));

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h3 className="text-base font-semibold text-slate-800">7-day sync activity</h3>
          <p className="text-xs text-slate-400 mt-0.5">Files picked up by the scheduled poll, per day</p>
        </div>
        <button
          onClick={() => setShowTable((v) => !v)}
          className="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1 transition-colors"
        >
          {showTable ? 'Chart' : 'Table'}
        </button>
      </div>

      {/* Legend: always present for two or more series, so identity never rests on colour alone */}
      <div className="flex items-center gap-4 mt-3 mb-4">
        {LEGEND.map(({ key, label, color }) => (
          <span key={key} className="flex items-center gap-1.5 text-xs text-slate-500">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden="true" />
            {label}
          </span>
        ))}
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs text-slate-500">
                <th className="text-left font-medium py-2 pr-3">Date</th>
                {['New', 'Modified', 'Deleted', 'Errors', 'Runs'].map((h) => (
                  <th key={h} className="text-right font-medium py-2 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {weekly.map((d) => (
                <tr key={d.date} className="border-b border-slate-50 last:border-0">
                  <td className="py-2 pr-3 text-slate-600">{d.label}</td>
                  <td className="py-2 px-3 text-right text-slate-700">{fmt(d.new)}</td>
                  <td className="py-2 px-3 text-right text-slate-700">{fmt(d.modified)}</td>
                  <td className="py-2 px-3 text-right text-slate-500">{fmt(d.deleted)}</td>
                  <td className="py-2 px-3 text-right text-slate-500">{fmt(d.errors)}</td>
                  <td className="py-2 px-3 text-right text-slate-500">{fmt(d.runs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative">
          <div className="flex">
            {/* Y axis — ticks carry the values that are not directly labelled */}
            <div
              className="relative w-10 shrink-0 text-[11px] text-slate-400 tabular-nums"
              style={{ height: PLOT_HEIGHT }}
            >
              {ticks.map((t) => (
                <span
                  key={t}
                  className="absolute right-2 -translate-y-1/2"
                  style={{ top: `${(1 - t / axisTop) * 100}%` }}
                >
                  {fmtCompact(t)}
                </span>
              ))}
            </div>

            <div className="relative flex-1" style={{ height: PLOT_HEIGHT }}>
              {/* Solid hairline gridlines, one step off the surface — never dashed */}
              {ticks.map((t) => (
                <div
                  key={t}
                  className="absolute left-0 right-0"
                  style={{
                    top: `${(1 - t / axisTop) * 100}%`,
                    height: 1,
                    backgroundColor: t === 0 ? CHROME.baseline : CHROME.grid,
                  }}
                  aria-hidden="true"
                />
              ))}

              <div className="absolute inset-0 flex items-end">
                {weekly.map((day, i) => {
                  const newVal = day.new || 0;
                  const modVal = day.modified || 0;
                  const total = newVal + modVal;
                  const newH = (newVal / axisTop) * PLOT_HEIGHT;
                  const modH = (modVal / axisTop) * PLOT_HEIGHT;
                  const isActive = active === i;

                  return (
                    <button
                      key={day.date}
                      type="button"
                      // The whole column band is the hit target, not just the painted bar
                      className="group relative flex-1 h-full flex flex-col justify-end items-center focus:outline-none"
                      onMouseEnter={() => setActive(i)}
                      onMouseLeave={() => setActive(null)}
                      onFocus={() => setActive(i)}
                      onBlur={() => setActive(null)}
                      aria-label={`${day.label}: ${fmt(newVal)} new, ${fmt(modVal)} modified`}
                    >
                      {/* Selective direct label — only the peak day, never a number on every column */}
                      {i === peakIndex && total > 0 && (
                        <span
                          className="absolute text-[11px] font-medium text-slate-500"
                          style={{ bottom: newH + modH + (modVal > 0 ? SEGMENT_GAP : 0) + 6 }}
                        >
                          {fmtCompact(total)}
                        </span>
                      )}

                      <div
                        className="w-full flex flex-col justify-end transition-opacity"
                        style={{
                          maxWidth: BAR_MAX_WIDTH,
                          opacity: active === null || isActive ? 1 : 0.55,
                        }}
                      >
                        {modVal > 0 && (
                          <div
                            style={{
                              height: Math.max(modH, 2),
                              backgroundColor: SERIES.modified,
                              borderRadius: '4px 4px 0 0',
                              marginBottom: newVal > 0 ? SEGMENT_GAP : 0,
                            }}
                          />
                        )}
                        {newVal > 0 && (
                          <div
                            style={{
                              height: Math.max(newH, 2),
                              backgroundColor: SERIES.new,
                              borderRadius: modVal > 0 ? 0 : '4px 4px 0 0',
                            }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* X axis */}
          <div className="flex mt-2">
            <div className="w-10 shrink-0" />
            <div className="flex-1 flex">
              {weekly.map((day, i) => (
                <span
                  key={day.date}
                  className={`flex-1 text-center text-[11px] ${
                    i === weekly.length - 1 ? 'text-slate-600 font-medium' : 'text-slate-400'
                  }`}
                >
                  {day.label}
                </span>
              ))}
            </div>
          </div>

          {isEmpty && (
            <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-slate-400">
              No sync activity in the last 7 days
            </p>
          )}

          {/* Tooltip: the value leads, the series name follows; series keyed by a short stroke */}
          {active !== null && (
            <div
              className="pointer-events-none absolute z-10 rounded-lg bg-slate-900 text-white shadow-lg px-3 py-2 text-xs"
              style={{
                left: `calc(2.5rem + ${((active + 0.5) / weekly.length) * 100}% )`,
                transform: 'translateX(-50%)',
                bottom: PLOT_HEIGHT * 0.55,
                minWidth: 148,
              }}
              role="status"
            >
              <p className="font-medium mb-1.5 text-slate-200">{weekly[active].label}</p>
              {LEGEND.map(({ key, label, color }) => (
                <p key={key} className="flex items-center gap-2 leading-5">
                  <span className="w-3 h-0.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="font-semibold tabular-nums">{fmt(weekly[active][key])}</span>
                  <span className="text-slate-400">{label}</span>
                </p>
              ))}
              {(weekly[active].deleted > 0 || weekly[active].errors > 0) && (
                <p className="mt-1.5 pt-1.5 border-t border-slate-700 text-slate-300 leading-5">
                  {weekly[active].deleted > 0 && <>{fmt(weekly[active].deleted)} deleted </>}
                  {weekly[active].errors > 0 && <>· {fmt(weekly[active].errors)} errors</>}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncActivityChart;
